import { useCallback } from "react";

import { match, P } from "ts-pattern";

import FlexWrapper, { AlignItems, FlexDirection } from "@gx/dls/containers/FlexWrapper";
import Wrapper from "@gx/dls/containers/Wrapper";
import Text, { TextVariant } from "@gx/dls/text/Text";
import { ProjectMappingMethod } from "@gx/ts-contracts/services/project/resources/project_configuration/project_configuration_pb";

import { CreateProjectFormActionType } from "@/pages/projects/forms/create/actions";
import MappingMethodAutoIntrospect from "@/pages/projects/forms/create/components/steps/select-mapping-method/components/MappingMethodAutoIntrospect";
import MappingMethodCard from "@/pages/projects/forms/create/components/steps/select-mapping-method/components/MappingMethodCard";
import { useCreateProjectFormContext } from "@/pages/projects/forms/create/hooks";

import { FeatureFlag, useFeatureFlag } from "@/hooks/useFeatureFlag";

const SelectMappingMethodBody = () => {
  const { state, dispatch } = useCreateProjectFormContext();
  const selectedMethod = state.selectedMappingMethod;

  const isJsonEnabled = useFeatureFlag(FeatureFlag.PROJECT_MAPPING_METHOD_JSON_ENABLED);
  const isBlankEnabled = useFeatureFlag(FeatureFlag.PROJECT_MAPPING_METHOD_BLANK_ENABLED);

  const handleSelectMethod = useCallback(
    (method: ProjectMappingMethod) => {
      dispatch({
        type: CreateProjectFormActionType.SET_SELECTED_MAPPING_METHOD,
        payload: method,
      });
    },
    [dispatch],
  );

  const renderMainContent = useCallback(() => {
    return match(selectedMethod)
      .returnType<React.ReactNode>()
      .with(ProjectMappingMethod.AUTO, () => <MappingMethodAutoIntrospect />)
      .with(ProjectMappingMethod.JSON, () => (
        <Wrapper fillWidth>
          <MappingMethodCard
            method={ProjectMappingMethod.JSON}
            isSelected={true}
            onSelect={() => handleSelectMethod(ProjectMappingMethod.JSON)}
          />
        </Wrapper>
      ))
      .with(ProjectMappingMethod.BLANK, () => (
        <Wrapper fillWidth>
          <MappingMethodCard
            method={ProjectMappingMethod.BLANK}
            isSelected={true}
            onSelect={() => handleSelectMethod(ProjectMappingMethod.BLANK)}
          />
        </Wrapper>
      ))
      .with(ProjectMappingMethod.UNSPECIFIED, () => <MappingMethodAutoIntrospect />)
      .with(P.number, () => <MappingMethodAutoIntrospect />)
      .exhaustive();
  }, [selectedMethod, handleSelectMethod]);

  return (
    <FlexWrapper direction={FlexDirection.COLUMN} gap={16} fillWidth>
      {renderMainContent()}
      {isJsonEnabled ||
        (isBlankEnabled && (
          <FlexWrapper gap={12} alignItems={AlignItems.CENTER}>
            <Text variant={TextVariant.TERTIARY}>Use a different mapping method</Text>
            <FlexWrapper alignItems={AlignItems.CENTER} gap={8}>
              {isJsonEnabled && (
                <MappingMethodCard
                  method={ProjectMappingMethod.JSON}
                  isSelected={false}
                  onSelect={() => handleSelectMethod(ProjectMappingMethod.JSON)}
                />
              )}
              {isBlankEnabled && (
                <MappingMethodCard
                  method={ProjectMappingMethod.BLANK}
                  isSelected={false}
                  onSelect={() => handleSelectMethod(ProjectMappingMethod.BLANK)}
                />
              )}
            </FlexWrapper>
          </FlexWrapper>
        ))}
    </FlexWrapper>
  );
};

export default SelectMappingMethodBody;
